import React from 'react';
import {
  Box, Button, Table, TableBody, TableCell, TableContainer, TableHead, TableRow,
  Paper, Typography, IconButton, Tooltip, Chip,
} from '@mui/material';
import { useTheme, alpha } from '@mui/material/styles';
import {
  Delete as DeleteIcon, Save as SaveIcon, ReceiptLong as ReceiptLongIcon, Inventory2Outlined as EmptyIcon,
} from '@mui/icons-material';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';

const calcularVistaLinea = (d, tipoDte, esNuevo) => {
  const cant = parseFloat(d.cantidad) || 0;
  const precBase = parseFloat(d.precio_unitario) || 0;
  const descPct = parseFloat(d.porcentaje_descuento) || 0;
  const descMontoBase = parseFloat(d.monto_descuento) || (cant * precBase * (descPct / 100));

  const esSinIva = tipoDte === '03' || tipoDte === '11';
  const precMostrar = esSinIva ? (precBase / 1.13) : precBase;
  const descMontoMostrar = esSinIva ? (descMontoBase / 1.13) : descMontoBase;
  const subtotalLinea = (cant * precMostrar) - descMontoMostrar;

  let tituloPrincipal = d.descripcion || d.nombre_item || '';
  let notaExtendida = d.observaciones || '';

  if (d.observaciones && d.observaciones.includes(' | ')) {
    const partes = d.observaciones.split(' | ');
    tituloPrincipal = partes[0];
    notaExtendida = partes.slice(1).join(' | ');
  } else if (!esNuevo && d.observaciones) {
    tituloPrincipal = d.observaciones;
    notaExtendida = '';
  }

  return { cant, precMostrar, descPct, descMontoMostrar, subtotalLinea, tituloPrincipal, notaExtendida };
};

const EstadoVacio = () => (
  <Box sx={{ py: 7, display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 1 }}>
    <Box sx={{ p: 2, borderRadius: '50%', bgcolor: 'action.hover', mb: 1 }}>
      <EmptyIcon sx={{ fontSize: 40, color: 'text.secondary' }} />
    </Box>
    <Typography color="text.primary" fontWeight={600}>
      Aún no hay productos o servicios agregados
    </Typography>
    <Typography variant="caption" color="text.secondary">
      Usa el buscador superior para seleccionar ítems y añadirlos al comprobante.
    </Typography>
  </Box>
);

const AlbaranDetalleTable = ({
  detalles,
  esEditable,
  esNuevo,
  tipoDte,
  selectedPaciente,
  onEliminarLinea,
  guardando,
  onGuardarAlbaran,
}) => {
  const theme = useTheme();
  const columnas = 6 + (esEditable ? 1 : 0);

  return (
    <>
      <TableContainer
        component={Paper}
        elevation={0}
        sx={{
          mb: 3,
          borderRadius: 3,
          overflow: 'hidden',
          border: `1px solid ${alpha(theme.palette.divider, 0.8)}`,
          boxShadow: '0 4px 20px 0 rgba(0,0,0,0.03)',
        }}
      >
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            justify: 'space-between',
            px: 2.5,
            py: 2,
            borderBottom: `1px solid ${theme.palette.divider}`,
            bgcolor: alpha(theme.palette.common.black, 0.01),
          }}
        >
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
            <Box sx={{ p: 0.8, borderRadius: 1.5, bgcolor: alpha(theme.palette.primary.main, 0.08), color: 'primary.main', display: 'flex' }}>
              <ReceiptLongIcon fontSize="small" />
            </Box>
            <Typography variant="subtitle1" fontWeight={700}>
              Detalle del Comprobante
            </Typography>
          </Box>
          <Chip
            size="small"
            label={`${detalles.length} ${detalles.length === 1 ? 'línea' : 'líneas'}`}
            sx={{ fontWeight: 600, bgcolor: 'action.selected' }}
          />
        </Box>

        <Table sx={{ '& td, & th': { borderBottom: `1px solid ${alpha(theme.palette.divider, 0.5)}` } }}>
          <TableHead>
            <TableRow
              sx={{
                bgcolor: alpha(theme.palette.common.black, 0.02),
                '& th': {
                  fontWeight: 700,
                  color: 'text.secondary',
                  fontSize: '0.7rem',
                  textTransform: 'uppercase',
                  letterSpacing: '0.05em',
                  py: 1.5,
                },
              }}
            >
              <TableCell>Paciente</TableCell>
              <TableCell>Descripción</TableCell>
              <TableCell align="right">Cantidad</TableCell>
              <TableCell align="right">Precio Unit.</TableCell>
              <TableCell align="center">Descuento</TableCell>
              <TableCell align="right">Subtotal</TableCell>
              {esEditable && <TableCell align="center">Acción</TableCell>}
            </TableRow>
          </TableHead>
          <TableBody>
            {detalles.length === 0 ? (
              <TableRow>
                <TableCell colSpan={columnas} align="center" sx={{ border: 0 }}>
                  <EstadoVacio />
                </TableCell>
              </TableRow>
            ) : (
              detalles.map((d) => {
                const {
                  cant, precMostrar, descPct, descMontoMostrar, subtotalLinea,
                  tituloPrincipal, notaExtendida,
                } = calcularVistaLinea(d, tipoDte, esNuevo);

                return (
                  <TableRow
                    key={d.id_detalle}
                    sx={{
                      transition: 'background-color 0.2s ease',
                      '&:hover': { bgcolor: alpha(theme.palette.primary.main, 0.02) },
                      '&:last-child td': { borderBottom: 0 },
                    }}
                  >
                    <TableCell>
                      <Typography variant="body2" fontWeight={500} color="text.secondary">
                        {d.nombre_paciente || d.paciente_nombre || d.nombre || selectedPaciente?.nombre_paciente || '-'}
                      </Typography>
                    </TableCell>

                    <TableCell sx={{ maxWidth: 320 }}>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                        <Typography variant="body2" fontWeight={600} color="text.primary">
                          {tituloPrincipal}
                        </Typography>

                        <Tooltip title="Copiar descripción" arrow>
                          <IconButton
                            size="small"
                            onClick={() => {
                              const texto = [tituloPrincipal, notaExtendida].filter(Boolean).join(' /&/ ');
                              navigator.clipboard.writeText(texto);
                            }}
                            sx={{ p: 0.25, color: 'text.disabled', '&:hover': { color: 'primary.main' } }}
                          >
                            <ContentCopyIcon style={{ fontSize: 14 }} />
                          </IconButton>
                        </Tooltip>
                      </Box>

                      {notaExtendida && (
                        <Typography variant="caption" display="block" sx={{ color: 'text.secondary', mt: 0.2 }}>
                          📝 {notaExtendida}
                        </Typography>
                      )}
                    </TableCell>

                    <TableCell align="right">
                      <Typography variant="body2" fontWeight={600}>
                        {cant}
                      </Typography>
                    </TableCell>
                    
                    <TableCell align="right">
                      <Typography variant="body2" color="text.secondary">
                        ${precMostrar.toFixed(2)}
                      </Typography>
                    </TableCell>
                    
                    <TableCell align="center">
                      {descMontoMostrar > 0 ? (
                        <Chip
                          size="small"
                          label={`-${descPct.toFixed(0)}% (${descMontoMostrar.toFixed(2)}$)`}
                          sx={{
                            fontWeight: 700,
                            fontSize: '0.7rem',
                            bgcolor: alpha(theme.palette.error.main, 0.08),
                            color: 'error.main',
                            border: 'none',
                          }}
                        />
                      ) : (
                        <Typography variant="caption" color="text.disabled">
                          —
                        </Typography>
                      )}
                    </TableCell>
                    
                    <TableCell align="right">
                      <Typography variant="body2" fontWeight={700} color="text.primary">
                        ${subtotalLinea.toFixed(2)}
                      </Typography>
                    </TableCell>

                    {esEditable && (
                      <TableCell align="center">
                        <Tooltip title="Eliminar línea" arrow>
                          <IconButton
                            size="small"
                            onClick={() => onEliminarLinea(d.id_detalle)}
                            sx={{
                              color: 'text.secondary',
                              '&:hover': { bgcolor: alpha(theme.palette.error.main, 0.08), color: 'error.main' },
                            }}
                          >
                            <DeleteIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      </TableCell>
                    )}
                  </TableRow>
                );
              })
            )}
          </TableBody>
        </Table>
      </TableContainer>

      {esNuevo && (
        <Box sx={{ mb: 3, display: 'flex', justifyContent: 'flex-end' }}>
          <Button
            variant="contained"
            color="primary"
            size="large"
            startIcon={<SaveIcon />}
            disabled={guardando}
            onClick={onGuardarAlbaran}
            sx={{
              px: 4,
              py: 1.2,
              borderRadius: 2.5,
              fontWeight: 700,
              textTransform: 'none',
              boxShadow: `0 8px 16px ${alpha(theme.palette.primary.main, 0.2)}`,
            }}
          >
            {guardando ? 'Guardando...' : 'Guardar Albarán'}
          </Button>
        </Box>
      )}
    </>
  );
};

export default AlbaranDetalleTable;